/**
 * The office app frame: header, left nav, page bar, content. Every office page
 * renders inside this (plan §3.4); the Sales and Crew apps have their own
 * frames and do not show the left nav.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { MeResponse, NavKey } from '@rowted/shared'
import { Header } from './header.js'
import { SupportBanner } from './support-banner.js'
import { DemoBanner } from './demo-banner.js'
import { LeftNav, NavSheet, type CreateKind, type NavBadge } from './nav.js'
import { applyTheme, chooseTheme, readTheme, type Theme } from '../lib/theme.js'
import { usePhone } from '../lib/use-narrow.js'

const COLLAPSED_KEY = 'rowted.nav.collapsed'

export interface AppFrameProps {
  me: MeResponse
  /**
   * Which rail item is current, or null when the page has none — People and
   * the account screens are reached from the menu, not the rail, and
   * highlighting whichever item happens to be first says they are somewhere
   * they are not.
   */
  active: NavKey | null
  badges?: Partial<Record<NavKey, NavBadge>>
  children: ReactNode
  onNavigate?: (key: NavKey) => void
  onSignOut?: () => void
  onScopeChange?: (orgUnitIds: string[]) => void
  onOpenSearch?: () => void
  unreadCount?: number
  /**
   * The nav's **New** (`new-request.md` §13.2): given only when the person may
   * take a request, so Rowted Admin and everybody else see the nav unchanged.
   */
  onCreate?: (kind: CreateKind) => void
  /** Whether its Create menu is open — the **N** key opens it from the shell. */
  createOpen?: boolean
  onCreateOpenChange?: (open: boolean) => void
}

export function AppFrame({
  me, active, badges, children, onNavigate, onSignOut, onScopeChange, onOpenSearch, unreadCount,
  onCreate, createOpen, onCreateOpenChange,
}: AppFrameProps) {
  const [theme, setTheme] = useState<Theme>(() => readTheme())

  // The nav's collapsed state is remembered per device, and an iPad starts
  // collapsed (plan §3.4.2) — a narrow window, or a finger at any width: an
  // iPad on its side is 1,180 px and is still an iPad (Activity, Board 12 A).
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY)
    if (stored !== null) return stored === 'true'
    return window.matchMedia('(max-width: 1100px), (pointer: coarse)').matches
  })

  /*
   * On a phone the rail is collapsed whatever this device remembers.
   *
   * 224px of nav out of 390px of screen leaves 166px for the work, so every
   * page overflowed sideways and the whole app slid around under the thumb.
   * The remembered choice is not overwritten — turn the phone on its side or
   * open the same account on a laptop and the rail is out again, because what
   * was stored was a choice about a wide screen.
   */
  const phone = usePhone()
  const [navOpen, setNavOpen] = useState(false)
  /*
   * The nav is the device's, never the page's: the same rail beside every
   * page, or on a phone the same sheet. Activity used to ask for touch rows on
   * an iPad on its side and ☰ held upright, so on a touch screen the nav grew
   * on Activity and shrank on the next page (Aaron, Nov 1).
   */
  const sheetNav = phone

  // A phone that becomes a tablet — a rotation, or a desktop window dragged
  // wider — gets its rail back, and a sheet still open over it would be a
  // second navigation on top of the first.
  useEffect(() => { if (!sheetNav) setNavOpen(false) }, [sheetNav])

  useEffect(() => applyTheme(theme), [theme])
  useEffect(() => localStorage.setItem(COLLAPSED_KEY, String(collapsed)), [collapsed])

  return (
    <div className="flex h-app flex-col bg-bg">
      {/*
        * Above the header, not inside it. §6.12 wants it persistent and
        * unmissable, and the header is where the customer's own tools live —
        * a notice about somebody else being in the account does not belong
        * among them.
        */}
      {me.demo && <DemoBanner demo={me.demo} />}
      {me.impersonation && <SupportBanner support={me.impersonation} />}
      <Header
        me={me}
        theme={theme}
        /* A pick from the menu is the one thing remembered (step 27). */
        onThemeChange={(next) => { chooseTheme(next); setTheme(next) }}
        /*
         * Undefined unless a caller sets one, so the header's own bell count
         * shows (§26.6: the bell rings). `?? 0` here overrode it with a nought
         * on every office page, and the badge never appeared.
         */
        unreadCount={unreadCount}
        onOpenSearch={onOpenSearch ?? (() => {})}
        onSignOut={onSignOut ?? (() => {})}
        onScopeChange={onScopeChange ?? (() => {})}
        phone={phone}
        menu={sheetNav}
        navOpen={navOpen}
        onOpenNav={() => setNavOpen(true)}
      />

      <div className="flex min-h-0 flex-1">
        {/*
          * On a phone the rail is not collapsed — it is gone, and opens as a
          * sheet from the header instead. Even at 60px it was a permanent
          * fifteenth of the screen spent on a control people touch twice an
          * hour, and it pushed every page's content into a column narrower
          * than the cards that go in it.
          */}
        {sheetNav
          ? (
            <NavSheet
              me={me}
              active={active}
              badges={badges ?? {}}
              open={navOpen}
              onClose={() => setNavOpen(false)}
              onNavigate={onNavigate ?? (() => {})}
              onCreate={onCreate}
            />
          )
          : (
            <LeftNav
              me={me}
              active={active}
              badges={badges ?? {}}
              collapsed={collapsed}
              onToggleCollapsed={() => setCollapsed((value) => !value)}
              onNavigate={onNavigate ?? (() => {})}
              onCreate={onCreate}
              createOpen={createOpen}
              onCreateOpenChange={onCreateOpenChange}
            />
          )}
        {/*
          * `relative` so nothing absolutely positioned inside a page can reach
          * the document.
          *
          * A `sr-only` label is `position: absolute`, and one with no
          * positioned ancestor is placed against the *document* at whatever
          * its static position happens to be — three of them in the
          * notification grid sat below the fold and gave the window a second
          * scrollbar of its own, which took the app's header off the top and
          * left blank space under it. Making this the containing block means
          * such a thing is scrolled and clipped by the page, like everything
          * else on it.
          */}
        <main className="relative min-w-0 flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  )
}
